import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { login as svcLogin, firstLogin as svcFirstLogin } from '@/services/auth.service';
import { useAuth } from '@/context/AuthContext';
import PasswordStrengthChecker, {
  isPasswordValid, passwordsMatch,
} from '@/pages/org-admin/users/PasswordStrengthChecker';
import { traduzErroAuth } from '@/utils/authErrors';

const GOLD_PRIMARY = '#F39C12';
const GOLD_DARK = '#E67E22';
const TEXT_DARK = '#2C3E50';

function FloatingPaths({ position }) {
  const paths = Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${380 - i * 5 * position} -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${152 - i * 5 * position} ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${684 - i * 5 * position} ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    width: 0.5 + i * 0.03,
  }));

  return (
    <div className="pointer-events-none absolute inset-0">
      <svg className="h-full w-full text-white" viewBox="0 0 696 316" fill="none">
        <title>Background Paths</title>
        {paths.map((path) => (
          <motion.path
            key={path.id}
            d={path.d}
            stroke="currentColor"
            strokeWidth={path.width}
            strokeOpacity={0.1 + path.id * 0.03}
            initial={{ pathLength: 0.3, opacity: 0.6 }}
            animate={{
              pathLength: 1,
              opacity: [0.3, 0.6, 0.3],
              pathOffset: [0, 1, 0],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'linear',
            }}
          />
        ))}
      </svg>
    </div>
  );
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const toggleCurrentPasswordVisibility = () => setShowCurrentPassword((prev) => !prev);
  const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await svcLogin(email, password);
      login(data.access_token, data.user);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 423) {
        setShowPasswordChange(true);
        toast.info('É necessário alterar sua senha no primeiro acesso');
      } else if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.detail;
        if (Array.isArray(validationErrors)) {
          const errorMessages = validationErrors
            .map((err) => {
              const field = err.loc?.[1] || err.loc?.[0] || 'campo';
              return `${field}: ${err.msg}`;
            })
            .join(', ');
          toast.error(`Erro de validação: ${errorMessages}`);
        } else {
          toast.error(error.response?.data?.detail || 'Dados inválidos. Verifique o formato do email.');
        }
      } else {
        toast.error(traduzErroAuth(error, 'Erro ao fazer login'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Mesmos critérios da tela de usuários (PasswordStrengthChecker).
    if (!isPasswordValid(newPassword)) {
      toast.error('A nova senha não atende aos requisitos de segurança');
      return;
    }

    if (!passwordsMatch(newPassword, confirmPassword)) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      const data = await svcFirstLogin({
        email,
        current_password: password,
        new_password: newPassword,
      });
      login(data.access_token, data.user);
      toast.success('Senha alterada com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(traduzErroAuth(error, 'Erro ao alterar senha'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative md:h-screen md:overflow-hidden lg:grid lg:grid-cols-2">
      {/* ====================== Painel ESQUERDO (decorativo) ====================== */}
      <div
        className="relative hidden h-full min-h-screen flex-col overflow-hidden border-r p-10 lg:flex"
        style={{
          background: `linear-gradient(135deg, ${GOLD_PRIMARY} 0%, ${GOLD_DARK} 100%)`,
          borderColor: 'rgba(255,255,255,0.15)',
        }}
      >
        {/* Overlay topo p/ transparente (legibilidade do header) */}
        <div
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            background:
              'linear-gradient(to top, transparent 0%, transparent 60%, rgba(230,126,34,0.55) 100%)',
          }}
        />

        {/* FloatingPaths SVG animado */}
        <div className="absolute inset-0">
          <FloatingPaths position={1} />
          <FloatingPaths position={-1} />
        </div>

        {/* Header brand */}
        <div className="z-20 flex items-center gap-2">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur-sm"
            style={{ boxShadow: '0 4px 14px -4px rgba(44,62,80,0.4)' }}
          >
            <Shield className="h-5 w-5 text-white" />
          </div>
          <p className="text-xl font-semibold tracking-wide text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            JURIS<span className="font-bold">KEY</span>
          </p>
        </div>

        {/* Quote bottom */}
        <div className="z-20 mt-auto">
          <blockquote className="space-y-3">
            <p className="text-xl leading-relaxed text-white">
              &ldquo;Centralize TOTP, automatize logins em tribunais e mantenha sua operação jurídica
              segura — tudo em um só lugar.&rdquo;
            </p>
            <footer className="font-mono text-sm font-semibold text-white/80">~ JurisKey</footer>
          </blockquote>
        </div>
      </div>

      {/* ====================== Painel DIREITO (form) ====================== */}
      <div className="relative flex min-h-screen flex-col justify-center p-4">
        {/* Blobs radiais decorativos (sutis, dourado) */}
        <div aria-hidden className="pointer-events-none absolute inset-0 isolate -z-10 opacity-60">
          <div
            className="absolute right-0 top-0 h-[80rem] w-[35rem] -translate-y-[22rem] rounded-full"
            style={{
              background:
                'radial-gradient(68.54% 68.72% at 55.02% 31.46%, rgba(243,156,18,0.08) 0%, rgba(230,126,34,0.02) 50%, rgba(243,156,18,0.01) 80%)',
            }}
          />
          <div
            className="absolute right-0 top-0 h-[80rem] w-[15rem] translate-x-[5%] -translate-y-1/2 rounded-full"
            style={{
              background:
                'radial-gradient(50% 50% at 50% 50%, rgba(243,156,18,0.06) 0%, rgba(243,156,18,0.01) 80%, transparent 100%)',
            }}
          />
          <div
            className="absolute right-0 top-0 h-[80rem] w-[15rem] -translate-y-[22rem] rounded-full"
            style={{
              background:
                'radial-gradient(50% 50% at 50% 50%, rgba(230,126,34,0.06) 0%, rgba(230,126,34,0.01) 80%, transparent 100%)',
            }}
          />
        </div>

        <div className="mx-auto w-full max-w-sm space-y-6">
          {/* Brand mobile only */}
          <div className="flex items-center gap-2 lg:hidden">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ background: `linear-gradient(135deg, ${GOLD_PRIMARY}, ${GOLD_DARK})` }}
            >
              <Shield className="h-5 w-5 text-white" />
            </div>
            <p className="text-xl font-semibold tracking-wide" style={{ color: TEXT_DARK }}>
              JURIS<span style={{ color: GOLD_PRIMARY }}>KEY</span>
            </p>
          </div>

          {/* Heading */}
          <div className="flex flex-col space-y-1">
            <h1
              className="text-2xl text-center font-bold tracking-wide mb-2"
              style={{ color: TEXT_DARK, fontFamily: 'Inter, sans-serif' }}
            >
              {showPasswordChange ? 'Alterar Senha' : 'Entrar na sua conta'}
            </h1>
            <p className="text-base text-center text-gray-500">
              {showPasswordChange
                ? 'Defina uma nova senha para continuar.'
                : 'Acesse o painel JurisKey com seu e-mail e senha'}
            </p>
          </div>

          {/* Form */}
          {!showPasswordChange ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Email */}
              <div className="relative h-max">
                <Input
                  id="email"
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="peer h-11 ps-9 transition-all focus-visible:border-[#F39C12] focus-visible:ring-[3px] focus-visible:ring-[#F39C12]/40"
                />
                <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-gray-400 peer-disabled:opacity-50">
                  <Mail className="h-4 w-4" aria-hidden="true" />
                </div>
              </div>

              {/* Password */}
              <div className="relative h-max">
                <Input
                  id="password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="peer h-11 ps-9 pe-10 transition-all focus-visible:border-[#F39C12] focus-visible:ring-[3px] focus-visible:ring-[#F39C12]/40"
                />
                <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-gray-400 peer-disabled:opacity-50">
                  <Lock className="h-4 w-4" aria-hidden="true" />
                </div>
                <button
                  type="button"
                  onClick={toggleCurrentPasswordVisibility}
                  className="absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-gray-500 transition-colors hover:text-gray-700"
                  aria-label={showCurrentPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="h-11 w-full rounded-md font-semibold text-white transition-all hover:-translate-y-0.5 disabled:translate-y-0"
                style={{
                  background: `linear-gradient(135deg, ${GOLD_PRIMARY}, ${GOLD_DARK})`,
                  boxShadow: '0 8px 20px -6px rgba(243,156,18,0.55)',
                }}
              >
                {loading ? 'Entrando...' : 'Entrar no Sistema'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="newPassword" className="mb-2 block text-sm font-semibold text-gray-700">
                  Nova Senha
                </Label>
                <div className="relative h-max">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Digite sua nova senha"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="peer h-11 ps-9 pe-10 transition-all focus-visible:border-[#F39C12] focus-visible:ring-[3px] focus-visible:ring-[#F39C12]/40"
                  />
                  <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-gray-400 peer-disabled:opacity-50">
                    <Lock className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <button
                    type="button"
                    onClick={toggleNewPasswordVisibility}
                    className="absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-gray-500 transition-colors hover:text-gray-700"
                    aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="mb-2 block text-sm font-semibold text-gray-700">
                  Confirmar Nova Senha
                </Label>
                <div className="relative h-max">
                  <Input
                    id="confirmPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Confirme sua nova senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="peer h-11 ps-9 pe-10 transition-all focus-visible:border-[#F39C12] focus-visible:ring-[3px] focus-visible:ring-[#F39C12]/40"
                  />
                  <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-gray-400 peer-disabled:opacity-50">
                    <Lock className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <button
                    type="button"
                    onClick={toggleNewPasswordVisibility}
                    className="absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-gray-500 transition-colors hover:text-gray-700"
                    aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <PasswordStrengthChecker
                password={newPassword}
                confirm={confirmPassword}
                showMatch
              />

              <Button
                type="submit"
                disabled={
                  loading
                  || !isPasswordValid(newPassword)
                  || !passwordsMatch(newPassword, confirmPassword)
                }
                className="h-11 w-full rounded-md font-semibold text-white transition-all hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-60"
                style={{
                  background: `linear-gradient(135deg, ${GOLD_PRIMARY}, ${GOLD_DARK})`,
                  boxShadow: '0 8px 20px -6px rgba(243,156,18,0.55)',
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-white" />
                    Alterando...
                  </div>
                ) : (
                  'Alterar Senha'
                )}
              </Button>
            </form>
          )}

          {/* Footer compacto */}
          <p className="mt-8 text-center text-xs text-gray-400">
            © 2026 JURISKEY. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </main>
  );
};

export default Login;
